// 라인 구분 스트리밍(NDJSON·SSE) 본문을 프레임 단위로 읽는 단일 출처.
//
// 채팅(NDJSON)·모니터링 어시스트(SSE)·유지하기 영향분석(SSE) 세 곳이 각자
// `res.body.getReader()` + `TextDecoder` + 버퍼링 + 구분자 분할을 손수 구현하다
// 드리프트했다(SSE인데 한쪽은 `\n\n`, 다른 쪽은 `\n`으로 분할·말미 flush 유무 제각각).
// 여기 한 곳으로 통일한다. 청크 경계가 프레임 중간을 자를 수 있으므로 버퍼링하며
// 완성된 프레임만 내보내고, 마지막 개행 없는 잔여도 flush한다.

/**
 * Response 본문을 `delimiter`로 구분된 프레임의 async iterator로 읽는다.
 *
 * - NDJSON: `delimiter='\n'` → 프레임 = JSON 한 줄.
 * - SSE:    `delimiter='\n'`로 읽고 각 프레임을 {@link sseData}로 거르면
 *   `data:` 페이로드만 얻는다(빈 줄·이벤트 구분 개행은 자동 무시).
 *
 * 취소는 fetch에 넘긴 AbortSignal이 담당한다(abort 시 read()가 reject → finally로 lock 해제).
 */
export async function* readDelimitedStream(
    body: ReadableStream<Uint8Array>,
    delimiter = '\n'
): AsyncGenerator<string, void, void> {
    const reader = body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    try {
        for (;;) {
            const { value, done } = await reader.read()
            if (done) break
            buffer += decoder.decode(value, { stream: true })
            let idx: number
            while ((idx = buffer.indexOf(delimiter)) >= 0) {
                yield buffer.slice(0, idx)
                buffer = buffer.slice(idx + delimiter.length)
            }
        }
        // 마지막 개행 없는 잔여 프레임 처리.
        const tail = buffer + decoder.decode()
        if (tail) yield tail
    } finally {
        reader.releaseLock()
    }
}

/**
 * SSE 라인에서 `data:` 페이로드를 뽑는다(data 라인이 아니거나 비었으면 null).
 * 서버는 이벤트당 `data:` 한 줄에 JSON 하나를 싣는다(멀티라인 data 미사용).
 */
export function sseData(line: string): string | null {
    const trimmed = line.trim()
    if (!trimmed.startsWith('data:')) return null
    const raw = trimmed.slice(5).trim()
    return raw || null
}
